// app/components/home/News.tsx
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "next/navigation";
import {
  Container,
  Typography,
  Box,
  Grid,
  Divider,
} from "@mui/material";
import Link from "next/link";
import { useTranslations } from "next-intl";

interface NewsItem {
  id: string | number;
  title_zh: string;
  title_en: string;
  subtitle_zh: string;
  subtitle_en: string;
  img1: string;
  img2: string;
  [key: string]: any;
  date: string;
  category_zh: string;
  category_en: string;
  descriptionZh: string;
  descriptionEn: string;
  slug_zh: string;
  slug_en: string;

  coverDesktopUrl?: string;
  coverTabletUrl?: string;
  coverMobileUrl?: string;
  galleryDesktopUrls?: string[];
  galleryTabletUrls?: string[];
  galleryMobileUrls?: string[];
}

function getBackgroundImage({ src }: { src: string }) {
  return {
    backgroundImage: `url(${src})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
}

function sortByLatest(items: NewsItem[]) {
  return [...items].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();

    if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
      return dateB - dateA;
    }

    return Number(b.id) - Number(a.id);
  });
}

export default function News() {
  const t = useTranslations("News");
  const params = useParams();
  const locale = (params?.locale as string) || "en";
  const isEnglish = locale === "en";

  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/news?locale=${locale}`, {
          cache: "no-store",
        });

        if (!res.ok) {
          console.error("[News] fetch failed:", res.status);
          setNewsList([]);
          return;
        }

        const data = await res.json();
        const rows: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : [];

        const normalized: NewsItem[] = rows.map((row) => ({
          ...row,
          img1: row.img1 ?? row.coverImageUrl ?? "",
          img2: row.img2 ?? "",
        }));

        setNewsList(normalized);
      } catch (err) {
        console.error("[News] fetch error:", err);
        setNewsList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [locale]);

  const latestNews = useMemo(() => {
    return sortByLatest(newsList).slice(0, 3);
  }, [newsList]);

  const buildDetailUrl = (slug: string) => {
    return `/${locale}/news/detail/${encodeURIComponent(slug)}`;
  };

  if (loading || latestNews.length === 0) {
    return null;
  }

  return (
    <Box id="news" sx={{ backgroundColor: "#f8f6ef" }}>
      <Container sx={{ pb: { xs: 3, md: 8 }, pt: { xs: 3, md: 5 } }}>
        <Grid
          container
          sx={{
            display: "flex",
            alignItems: { xs: "start", md: "center" },
            mb: { xs: 3, md: 2 }
          }}>
          <Grid size={12}>
            <Typography component="h4" variant="h4">
              {t("latest_contents")}
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {latestNews.map((item) => (
            <Grid
              key={item.id}
              size={{ xs: 12, sm: 6, md: 4 }}
              className="card-news"
            >
              <Link
                href={buildDetailUrl(isEnglish ? item.slug_en : item.slug_zh)}
                style={{ display: "block", height: "100%" }}
              >
                <Grid container spacing={0} className="news">
                  <Grid
                    size={12}
                    className="news-image"
                    sx={getBackgroundImage({ src: item.img1 })}
                  />
                  <Grid size={12}>
                    <Box sx={{ p: 3 }}>
                      <Typography
                        variant="subtitle1"
                        title={isEnglish ? item.title_en : item.title_zh}
                        sx={{
                          mb: "1rem"
                        }}
                      >
                        {isEnglish ? item.title_en : item.title_zh}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    container
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 3,
                      pt: 0,
                      width: "100%",
                      mt: "auto"
                    }}>
                    <Grid size={12}>
                      <Divider />
                    </Grid>
                    <Grid size={6} className="date">
                      {item.date}
                    </Grid>
                  </Grid>
                </Grid>
              </Link>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}